/**
 * Şifre değiştirme formu — hem zorunlu değişiklik SAYFASI hem profil menüsünden açılan
 * POPUP bunu kullanıyor. İki kopya olsaydı doğrulama kuralları ayrışırdı.
 */
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { apiClient } from '@/shared/api/http/client';
import { ENDPOINTS } from '@/shared/api/endpoints';
import { useAuthStore } from '@/entities/session/model/authStore';
import { Button } from '@/shared/ui/Button';
import { PasswordInput } from '@/shared/ui/PasswordInput';
import { Label } from '@/shared/ui/Label';
import { Alert } from '@/shared/ui/Alert';

/** Yeni şifrenin en az uzunluğu. */
const MIN_LENGTH = 6;

interface Values {
  current: string;
  next: string;
  confirm: string;
}

export interface ChangePasswordFormProps {
  /** Başarıyla kaydedildiğinde: sayfa ana ekrana gider, popup kendini kapatır. */
  onSaved: () => void;
  /** Popup'ta ikinci bir düğme (Vazgeç) gösterilir. */
  onCancel?: () => void;
  idPrefix?: string;
}

export function ChangePasswordForm({ onSaved, onCancel, idPrefix = 'pwd' }: ChangePasswordFormProps) {
  const { t } = useTranslation();
  const user = useAuthStore((s) => s.user);
  const clearFlag = useAuthStore((s) => s.clearPasswordChangeFlag);
  const [error, setError] = useState<string | null>(null);
  const form = useForm<Values>();
  const errors = form.formState.errors;
  /*
   * Alan doğrulaması GÖRÜNÜR olmalı (#518). Kurallar (zorunlu, en az 6 karakter) vardı ama
   * mesajı yoktu: Kaydet'e basınca form yalnızca hatalı alana odaklanıyor, kullanıcı neden
   * kaydedilmediğini göremiyor ve "yeni şifre alanında takılı kaldı" sanıyordu.
   */
  const fieldError = (name: keyof Values): string | null => {
    const e = errors[name];
    if (!e) return null;
    if (e.type === 'minLength') return t('auth.changePassword.tooShort', { n: MIN_LENGTH });
    return t('auth.changePassword.required');
  };
  const errorLine = (name: keyof Values) => {
    const msg = fieldError(name);
    return msg ? (
      <p id={`${idPrefix}-${name}-err`} role="alert" className="text-danger text-xs">
        {msg}
      </p>
    ) : null;
  };

  async function onSubmit(v: Values) {
    setError(null);
    if (v.next !== v.confirm) {
      setError(t('auth.changePassword.mismatch'));
      return;
    }
    try {
      await apiClient.post(
        ENDPOINTS.SYSTEM_CHANGE_PASSWORD,
        { UserId: user?.userId, UserCode: user?.userCode, CurrentPassword: v.current, NewPassword: v.next },
        { octo: { expectSuccess: true } },
      );
      clearFlag();
      form.reset();
      onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  return (
    <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
      <div className="space-y-1">
        <Label htmlFor={`${idPrefix}-cur`}>{t('auth.changePassword.current')}</Label>
        <PasswordInput
          id={`${idPrefix}-cur`}
          autoComplete="current-password"
          aria-invalid={!!errors.current || undefined}
          aria-describedby={errors.current ? `${idPrefix}-current-err` : undefined}
          {...form.register('current', { required: true })}
        />
        {errorLine('current')}
      </div>
      <div className="space-y-1">
        <Label htmlFor={`${idPrefix}-new`}>{t('auth.changePassword.new')}</Label>
        <PasswordInput
          id={`${idPrefix}-new`}
          autoComplete="new-password"
          aria-invalid={!!errors.next || undefined}
          aria-describedby={errors.next ? `${idPrefix}-next-err` : `${idPrefix}-next-hint`}
          {...form.register('next', { required: true, minLength: MIN_LENGTH })}
        />
        {errorLine('next') ?? (
          <p id={`${idPrefix}-next-hint`} className="text-fg-subtle text-xs">
            {t('auth.changePassword.hint', { n: MIN_LENGTH })}
          </p>
        )}
      </div>
      <div className="space-y-1">
        <Label htmlFor={`${idPrefix}-conf`}>{t('auth.changePassword.confirm')}</Label>
        <PasswordInput
          id={`${idPrefix}-conf`}
          autoComplete="new-password"
          aria-invalid={!!errors.confirm || undefined}
          aria-describedby={errors.confirm ? `${idPrefix}-confirm-err` : undefined}
          {...form.register('confirm', { required: true })}
        />
        {errorLine('confirm')}
      </div>
      {error ? <Alert tone="danger">{error}</Alert> : null}
      <div className="flex gap-2">
        {onCancel ? (
          <Button type="button" variant="secondary" className="flex-1" onClick={onCancel}>
            {t('common:Cancel')}
          </Button>
        ) : null}
        <Button type="submit" className="flex-1" loading={form.formState.isSubmitting}>
          {t('auth.changePassword.save')}
        </Button>
      </div>
    </form>
  );
}
